import {
  FireflyButton,
  ChakraFlex,
  ModalV2,
  Text,
  FAIconName,
  useToastMessage,
  EditorV2,
  coalescePlainTextToEditorV2Html,
  EditorV2State,
  ChakraGrid,
  ChakraGridItem,
} from '@equip.health/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateDischargePlanMutation } from '~/store/services/clinical-data/dischargePlan';
import { useDiscardChangesModalContext } from '../common/DiscardChangesModalContext';
import { AftercareFieldName } from '../sections/section-cards';
import { CharacterLimit } from '../common/dischargePlanConstants';

interface AddEditAftercareOrReferralsModalProps {
  dischargePlanId: string;
  isOpen: boolean;
  fieldName: AftercareFieldName;
  initialValue: string | null;
  fieldLabel?: string;
  placeholderText?: string;
}

const titleContextMap: Record<AftercareFieldName, string> = {
  aftercareBehavioral: 'behavioral recommendations',
  aftercareDietary: 'dietary recommendations',
  aftercareMedical: 'medical recommendations',
  aftercareOther: 'other recommendations',
  referralsAndFollowUpAppointments: 'referrals & follow-up appointments',
};

const AddEditAftercareOrReferralsModal = ({
  dischargePlanId,
  isOpen,
  fieldName,
  initialValue,
  fieldLabel,
  placeholderText = 'Please enter details',
}: AddEditAftercareOrReferralsModalProps) => {
  // rtk / hooks
  const [updateDischargePlan, { isLoading: isUpdateDischargePlanLoading }] =
    useUpdateDischargePlanMutation();
  const { createToast } = useToastMessage();
  const { onCloseModal } = useDiscardChangesModalContext();

  // refs / state
  const aftercareReferralsEditorRef = useRef<EditorV2State>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // constants
  const { submitButtonText, buttonIdPrefix, context, title } = useMemo(() => {
    const viewMode = initialValue ? 'Edit' : 'Add';
    const context = titleContextMap[fieldName] || undefined;
    const newTitle = `${viewMode} ${context}`;
    const newButtonIdPrefix = newTitle.toLowerCase().replace(/\s/g, '-');
    const submitButtonText =
      viewMode === 'Edit' ? 'Save changes' : 'Add to discharge plan';
    return {
      buttonIdPrefix: newButtonIdPrefix,
      context,
      submitButtonText,
      title: newTitle,
    };
  }, [fieldName, initialValue]);

  const htmlAftercareReferrals: string | null = initialValue
    ? coalescePlainTextToEditorV2Html(initialValue)
    : null;

  const handleSuccess = useCallback(() => {
    createToast({
      icon: FAIconName.checkRegular,
      id: `update-discharge-plan-${buttonIdPrefix}-success_toast`,
      text: `Updated ${context} successfully`,
      variant: 'success',
    });
  }, [buttonIdPrefix, context, createToast]);

  const handleFailure = useCallback(() => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: `update-discharge-plan-${buttonIdPrefix}-error_toast`,
      text: `Error updating ${context}`,
      variant: 'critical',
    });
  }, [buttonIdPrefix, context, createToast]);

  useEffect(() => {
    if (!isOpen) {
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setHasUnsavedChanges(false);
  }, [fieldName, initialValue]);

  return (
    <ModalV2
      footerContent={
        <ChakraFlex
          alignItems="center"
          justifyContent="space-between"
          marginTop="24px"
          width="100%"
        >
          <FireflyButton
            id={`${buttonIdPrefix}-cancel`}
            isLoading={isUpdateDischargePlanLoading}
            leftIcon={FAIconName.timesRegular}
            onClick={() => onCloseModal(hasUnsavedChanges)}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${buttonIdPrefix}-submit`}
            isDisabled={!hasUnsavedChanges}
            isLoading={isUpdateDischargePlanLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={() => {
              const value = aftercareReferralsEditorRef.current?.isEmpty
                ? ''
                : aftercareReferralsEditorRef.current?.getHTML();
              updateDischargePlan({
                dischargePlanId,
                dischargePlanPayload: {
                  [fieldName]: value,
                },
              })
                .unwrap()
                .then(() => {
                  handleSuccess();
                  onCloseModal(false);
                })
                .catch(() => handleFailure());
            }}
            variant="primary"
          >
            {submitButtonText}
          </FireflyButton>
        </ChakraFlex>
      }
      isOpen={isOpen}
      maxWidth="1000px"
      minWidth="1000px"
      onClose={() => onCloseModal(hasUnsavedChanges)}
      title={title}
    >
      <ChakraGrid gap="8px" templateColumns={{ base: 'repeat(1, 1fr)' }}>
        {fieldLabel && (
          <ChakraGridItem>
            <Text>{fieldLabel}</Text>
          </ChakraGridItem>
        )}
        <ChakraGridItem>
          {/** TODO add onFocus and onBlur Mixpanel metrics */}
          <EditorV2
            characterLimit={CharacterLimit}
            containerTestId={buttonIdPrefix}
            initialContent={htmlAftercareReferrals}
            onChange={(val) => {
              const hasChanged =
                val !== htmlAftercareReferrals &&
                !(
                  aftercareReferralsEditorRef.current?.isEmpty &&
                  htmlAftercareReferrals === null
                );
              setHasUnsavedChanges(hasChanged);
            }}
            placeholderText={placeholderText}
            stateRef={aftercareReferralsEditorRef}
          />
        </ChakraGridItem>
      </ChakraGrid>
    </ModalV2>
  );
};

export default AddEditAftercareOrReferralsModal;
